import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { useMemo } from 'react'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import {
  AppointmentCalendar,
  AppointmentCalendarIncluded,
  AppointmentCalendarMeta,
  AppointmentIncluded,
  AppointmentResMock,
  DoctorSummary
} from '../types/appointment.type'
import { GeneralResMock } from '../types/general.type'
import { StaffIncluded, StaffSAttrProps, StatisticsResMock } from '../types/staff.types'

type Props = {
  appointment_at?: string
  appointment_mode?: string
  appointment_type?: string
  date?: string
  staff_id?: string
  page?: number
  pageCount?: number
  status?: string
  query?: string
  id?: string
  facility_id?: string
  start_date?: string
  end_date?: string
  start_time?: string
  end_time?: string
}
export const useAppointment = ({
  appointment_mode,
  appointment_at,
  date,
  staff_id,
  page,
  pageCount,
  query,
  status,
  id,
  facility_id,
  appointment_type,
  start_date,
  end_date,
  start_time,
  end_time
}: Props = {}) => {
  const { data: availableDoctors, isLoading: staffIsLoading } = useQuery({
    enabled: !!appointment_at || !!facility_id,
    queryFn: async () => {
      const endPoint = 'appointments/available_doctors'
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<StaffSAttrProps[], StaffIncluded[]>>(endPoint, {
        params: {
          ...(facility_id && { facility_id }),
          ...(appointment_at && { appointment_at: appointment_at })
        }
      })
      return data
    },
    queryKey: ['appointment', { appointment_at, facility_id }]
    // refetchInterval: 60 * 1000, // Refresh every minute?
  })

  const { data: timeslots, isLoading: timeslotIsLoading } = useQuery({
    enabled: !!date && !!staff_id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<{ timeslots: { startTime: string; endTime: string }[] }>(
        'appointments/available_timeslots',
        {
          params: {
            ...(date && { date }),
            ...(staff_id && { staff_id }),
            ...(facility_id && { facility_id })
          }
        }
      )
      return data
    },
    queryKey: ['appointment', { date, staff_id, facility_id }]
  })

  const { data: appointmentDatas, isLoading } = useQuery({
    queryFn: async () => {
      const endpoint = 'appointments'
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<AppointmentResMock[], AppointmentIncluded[]>>(endpoint, {
        params: {
          ...(appointment_mode && { appointment_mode }),
          ...(appointment_type && { appointment_type }),
          ...(page && { 'page[number]': page }),
          ...(pageCount && { 'page[size]': pageCount }),
          ...(query && { query }),
          ...(status && { status }),
          ...(staff_id && { staff_id }),
          ...(appointment_at && { appointment_at }),
          ...(facility_id && { facility_id }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
          ...(start_time && { start_time }),
          ...(end_time && { end_time })
        }
      })
      return data
    },
    queryKey: [
      'appointments',
      {
        appointment_at,
        page,
        pageCount,
        query,
        staff_id,
        status,
        appointment_mode,
        appointment_type,
        facility_id,
        start_date,
        end_date,
        start_time,
        end_time
      }
    ]
  })

  const { data: appointmentStatistics } = useQuery({
    queryFn: async () => {
      const endpoint = 'appointments/statistics'
      const { data } = await AXIOS_INSTANCE.get<StatisticsResMock>(endpoint)
      return data.data
    },
    queryKey: ['appointments']
  })

  const { data: singleAppointment, isLoading: appointmentIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<AppointmentResMock, AppointmentIncluded[]>>(
        `appointments/${id}`
      )
      return data
    },
    queryKey: ['appointments', { id }]
  })

  const { mutateAsync: bookAppointment } = useMutation({
    mutationFn: async (formRec: any) => {
      const { data } = await AXIOS_INSTANCE.post('appointments', formRec, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['patient_dashboard'] })
    }
  })

  const { mutateAsync: updateAppointment } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      await AXIOS_INSTANCE.patch(`appointments/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['appointments'] })
  })

  const { mutateAsync: deleteAppointment } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`appointments/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['appointments'] })
  })

  const { data: appointmentCalendar, isLoading: appointmentCalendarIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<
        GeneralResMock<AppointmentCalendar[], AppointmentCalendarIncluded[], AppointmentCalendarMeta>
      >('appointments/calendar', { params: { date: date } })
      return data
    },
    queryKey: ['appointments', 'calendar', { date }],
    enabled: !!date
  })

  return {
    allAppointment: appointmentDatas?.data,
    appointmentCounts: appointmentDatas?.meta.pagination,
    appointmentIsLoading,
    appointmentStatistics,
    availableDoctors: availableDoctors?.data,
    bookAppointment,
    deleteAppointment,
    includedDatas: appointmentDatas?.included,
    isLoading,
    singleAppointment: singleAppointment?.data,
    singleIncludedData: singleAppointment?.included,
    staffIsLoading,
    timeslotIsLoading,
    timeslots,
    updateAppointment,
    appointmentCalendar: appointmentCalendar?.data,
    appointmentCalendarIncluded: appointmentCalendar?.included,
    appointmentCalendarIsLoading,
    doctorSummaries: appointmentCalendar?.meta.doctor_summaries
  }
}

/** Week starts Sunday; aligns with patient weekly calendar UI. */
export function startOfWeekSunday(d: Date): Date {
  const m = moment(d).clone().startOf('day')
  const dow = m.day()
  return m.subtract(dow, 'days').toDate()
}

export function useAppointmentCalendarWeek(anchorDate: Date) {
  const weekStart = useMemo(() => startOfWeekSunday(anchorDate), [anchorDate])
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => moment(weekStart).clone().add(i, 'day').format('YYYY-MM-DD')),
    [weekStart]
  )

  const queries = useQueries({
    queries: weekDates.map(date => ({
      queryKey: ['appointments', 'calendar', { date }],
      queryFn: async () => {
        const { data } = await AXIOS_INSTANCE.get<
          GeneralResMock<AppointmentCalendar[], AppointmentCalendarIncluded[], AppointmentCalendarMeta>
        >('appointments/calendar', { params: { date } })
        return data
      },
      staleTime: 60_000
    }))
  })

  const revision = queries.reduce((n, q) => n + (q.dataUpdatedAt ?? 0), 0)

  const merged = useMemo(() => {
    const entries: AppointmentCalendar[] = []
    const includedKey = new Set<string>()
    const included: AppointmentCalendarIncluded[] = []
    const docKey = new Set<number>()
    const doctorSummaries: DoctorSummary[] = []

    const entrySeen = new Set<string>()

    for (const q of queries) {
      const payload = q.data
      if (!payload) continue
      for (const row of payload.data ?? []) {
        if (entrySeen.has(row.id)) continue
        entrySeen.add(row.id)
        entries.push(row)
      }
      for (const row of payload.included ?? []) {
        const k = `${row.type}:${row.id}`
        if (!includedKey.has(k)) {
          includedKey.add(k)
          included.push(row)
        }
      }
      for (const row of payload.meta?.doctor_summaries ?? []) {
        if (!docKey.has(row.staff_id)) {
          docKey.add(row.staff_id)
          doctorSummaries.push(row)
        }
      }
    }
    return { doctorSummaries, entries, included }
  }, [revision, queries])

  const isLoading = queries.some(q => q.isPending)

  return {
    doctorSummaries: merged.doctorSummaries,
    entries: merged.entries,
    included: merged.included,
    isLoading,
    weekDates,
    weekStart
  }
}
